import { useState } from 'react';

import { ShieldKeyIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Permissions } from 'constants/permissions';
import { useSelector } from 'react-redux';

import { FloatingBanner } from 'components/FloatingBanner/FloatingBanner';
import { Button } from 'components/FormComponents';

import {
  useDenySupportAccessRequestMutation,
  useGetPendingSupportAccessRequestsQuery,
  useGrantSupportAccessRequestMutation,
} from 'api/support-access-api';
import { selectPermissions } from 'api/user-permissions-slice';

import { GrantAccessModal } from '../GrantAccessModal/GrantAccessModal';

const SupportAccessBanner = () => {
  const [dismissed, setDismissed] = useState(false);
  const [showGrantModal, setShowGrantModal] = useState(false);

  const permissions = useSelector(selectPermissions);
  const canManageCompany = permissions.includes(Permissions.CompanyWrite);

  const { data: pendingRequests, refetch } = useGetPendingSupportAccessRequestsQuery(undefined, {
    skip: !canManageCompany,
  });

  const [grantRequest, { isLoading: granting }] = useGrantSupportAccessRequestMutation();
  const [denyRequest, { isLoading: denying }] = useDenySupportAccessRequestMutation();

  const request = pendingRequests?.[0];

  if (!request || dismissed) return null;

  const handleGrant = async () => {
    try {
      await grantRequest(request.id).unwrap();
    } finally {
      refetch();
      setShowGrantModal(false);
    }
  };

  const handleDeny = async () => {
    try {
      await denyRequest(request.id).unwrap();
    } finally {
      refetch();
      setShowGrantModal(false);
    }
  };

  return (
    <FloatingBanner
      status="warning"
      icon={<HugeiconsIcon icon={ShieldKeyIcon} size={20} strokeWidth={1.5} color="currentColor" />}
      onClose={() => setDismissed(true)}>
      <div className="d-flex align-items-center gap-3">
        <div className="flex-grow-1">
          <p className="t-body u-font-weight-600 u-margin-bottom--small">Support Access Request</p>
          <p className="t-small secondary-text">
            Checkmate support has requested access to help troubleshoot
            {request.reason ? ' "' + request.reason + '"' : ' your account.'}
          </p>
        </div>
        <div className="d-flex gap-2 flex-shrink-0">
          <Button small type="secondary" onClick={handleDeny} loading={denying}>
            Deny Access
          </Button>
          <Button small type="primary" onClick={() => setShowGrantModal(true)}>
            Grant Access
          </Button>
        </div>
      </div>

      <GrantAccessModal
        visible={showGrantModal}
        request={request}
        granting={granting}
        denying={denying}
        onClose={() => setShowGrantModal(false)}
        onDeny={handleDeny}
        onGrant={handleGrant}
      />
    </FloatingBanner>
  );
};

export default SupportAccessBanner;
